import { useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { Download, Printer } from 'lucide-react';
import { AGEING_BUCKETS, ADJUSTMENT_REASONS, formatMoney, formatQty, PAYMENT_METHODS, type AdjustmentReason, type CurrencyCode, type PaymentMethod, type Permission } from '@tengesa/shared';
import { api } from '../../lib/api';
import { rangeFor, RANGE_OPTIONS, type RangeKey } from '../../lib/dates';
import { useSession } from '../../app/session';
import { Button, ErrorNotice, Field, PageHeader, Panel, Select, Spinner, TextInput } from '../../ui';
import { Tabs } from '../../ui/inputs';
import { BarList, ColumnChart } from '../../ui/charts';

type Tab = 'sales' | 'profit' | 'stock' | 'debtors' | 'creditors' | 'cash';
const TABS: { value: Tab; label: string; perm: Permission[] }[] = [
  { value: 'sales', label: 'Sales', perm: ['reports.sales'] },
  { value: 'profit', label: 'Profit', perm: ['reports.profit'] },
  { value: 'stock', label: 'Stock', perm: ['reports.stock'] },
  { value: 'debtors', label: 'Customers owe', perm: ['reports.financial', 'customers.view'] },
  { value: 'creditors', label: 'We owe', perm: ['reports.financial'] },
  { value: 'cash', label: 'Cash', perm: ['reports.financial', 'shifts.reconcile'] },
];

/** Downloads rows as a CSV file (opens in Excel). */
function downloadCsv(name: string, header: string[], rows: (string | number)[][]) {
  const esc = (v: string | number) => {
    const s = String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const csv = [header, ...rows].map((r) => r.map(esc).join(',')).join('\r\n');
  const url = URL.createObjectURL(new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = `${name}.csv`;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
const dec = (c: number) => (c / 100).toFixed(2);

export function ReportsPage() {
  const { me, canAny } = useSession();
  const tabs = TABS.filter((t) => canAny(...t.perm));
  const [tab, setTab] = useState<Tab>(tabs[0]?.value ?? 'sales');
  const [range, setRange] = useState<RangeKey>('7d');
  const [custom, setCustom] = useState(rangeFor('30d'));
  const [branchId, setBranchId] = useState('');
  const { from, to } = rangeFor(range, custom);
  const period = `from=${from}&to=${to}${branchId ? `&branchId=${branchId}` : ''}`;
  const usesPeriod = tab !== 'debtors' && tab !== 'creditors';
  return (
    <>
      <PageHeader title="Reports" description="Figures come straight from the sales, stock and account ledgers." actions={<Button icon={<Printer className="size-4" />} onClick={() => window.print()}>Print</Button>} />
      <Tabs value={tab} onChange={setTab} tabs={tabs} />
      {usesPeriod && (
        <div className="mb-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <Field label="Period" htmlFor="r-range">
            <Select id="r-range" value={range} onChange={(e) => setRange(e.target.value as RangeKey)}>
              {RANGE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </Select>
          </Field>
          {range === 'custom' && (
            <>
              <Field label="From" htmlFor="r-from"><TextInput id="r-from" type="date" value={custom.from} onChange={(e) => setCustom({ ...custom, from: e.target.value })} /></Field>
              <Field label="To" htmlFor="r-to"><TextInput id="r-to" type="date" value={custom.to} onChange={(e) => setCustom({ ...custom, to: e.target.value })} /></Field>
            </>
          )}
          {me.branches.length > 1 && (
            <Field label="Branch" htmlFor="r-branch">
              <Select id="r-branch" value={branchId} onChange={(e) => setBranchId(e.target.value)}>
                <option value="">All my branches</option>
                {me.branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
              </Select>
            </Field>
          )}
        </div>
      )}
      <div className="print-area">
        {usesPeriod && <p className="mb-3 hidden text-sm print:block">{me.business.tradingName ?? me.business.name} — {from} to {to}</p>}
        {tab === 'sales' && <SalesReport period={period} name={`sales-${from}-${to}`} />}
        {tab === 'profit' && <ProfitReport period={period} />}
        {tab === 'stock' && <StockReport period={period} />}
        {tab === 'debtors' && <DebtorsReport />}
        {tab === 'creditors' && <CreditorsReport />}
        {tab === 'cash' && <CashReport period={period} />}
      </div>
    </>
  );
}

function useMoney() {
  const { me } = useSession();
  const cur = me.business.baseCurrency as CurrencyCode;
  return (c: number) => formatMoney(c, cur);
}

function Loader<T>({ url, children }: { url: string; children: (d: T) => ReactNode }) {
  const q = useQuery({ queryKey: ['report', url], queryFn: () => api.get<T>(url) });
  if (q.isLoading) return <Spinner />;
  if (q.error) return <ErrorNotice error={q.error} onRetry={() => q.refetch()} />;
  return <>{children(q.data as T)}</>;
}

function Figures({ items }: { items: { label: string; value: string; note?: string; tone?: 'danger' | 'ok' }[] }) {
  return (
    <div className="mb-6 grid gap-px overflow-hidden rounded-[var(--radius-panel)] border border-line bg-line sm:grid-cols-2 lg:grid-cols-4">
      {items.map((i) => (
        <div key={i.label} className="bg-surface p-4">
          <p className="text-sm text-muted">{i.label}</p>
          <p className={clsx('mt-1 text-2xl font-semibold num', i.tone === 'danger' && 'text-danger', i.tone === 'ok' && 'text-ok')}>{i.value}</p>
          {i.note && <p className="text-sm text-muted">{i.note}</p>}
        </div>
      ))}
    </div>
  );
}

interface SalesData {
  showsProfit: boolean;
  totals: { saleCount: number; grossCents: number; discountCents: number; netSalesCents: number; vatCents: number; returnCount: number; returnsCents: number; revenueCents: number; costOfSalesCents: number; grossProfitCents: number; marginBp: number; averageSaleCents: number; outputVatCents: number };
  byDay: { date: string; saleCount: number; netSalesCents: number; returnsCents: number; revenueCents: number; grossProfitCents: number }[];
  byMethod: { method: string; amountCents: number }[];
  byCashier: { name: string; saleCount: number; netSalesCents: number }[];
  byCategory: { name: string; revenueCents: number; costCents: number; grossProfitCents: number }[];
  topProducts: { productId: string; name: string; unit: string; qtyMilli: number; netSalesCents: number; revenueCents: number; grossProfitCents: number }[];
}

function SalesReport({ period, name }: { period: string; name: string }) {
  const money = useMoney();
  return (
    <Loader<SalesData> url={`/api/reports/sales?${period}`}>
      {(d) => (
        <div className="space-y-6">
          <Figures
            items={[
              { label: 'Net sales (incl. VAT)', value: money(d.totals.netSalesCents), note: `${d.totals.saleCount} sales, average ${money(d.totals.averageSaleCents)}` },
              { label: 'Returns', value: money(d.totals.returnsCents), note: `${d.totals.returnCount} returns` },
              { label: 'Discounts given', value: money(d.totals.discountCents) },
              d.showsProfit
                ? { label: 'Gross profit', value: money(d.totals.grossProfitCents), note: `${(d.totals.marginBp / 100).toFixed(1)}% of ${money(d.totals.revenueCents)} revenue excl. VAT` }
                : { label: 'Output VAT', value: money(d.totals.outputVatCents) },
            ]}
          />
          <Panel title="Net sales by day" actions={
            <Button size="sm" icon={<Download className="size-4" />} onClick={() => downloadCsv(name, ['Date', 'Sales', 'Net sales', 'Returns', 'Revenue excl VAT', ...(d.showsProfit ? ['Gross profit'] : [])], d.byDay.map((x) => [x.date, x.saleCount, dec(x.netSalesCents), dec(x.returnsCents), dec(x.revenueCents), ...(d.showsProfit ? [dec(x.grossProfitCents)] : [])]))}>
              Download CSV
            </Button>
          }>
            <div className="p-5">
              <ColumnChart
                label="Net sales by day"
                data={d.byDay.map((x) => ({ key: x.date, label: new Date(`${x.date}T12:00:00Z`).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }), value: x.netSalesCents }))}
                format={money}
                detail={(i) => <p className="text-muted">{d.byDay[i]!.saleCount} sales{d.showsProfit ? `, ${money(d.byDay[i]!.grossProfitCents)} profit` : ''}</p>}
              />
            </div>
          </Panel>
          <div className="grid gap-6 lg:grid-cols-2">
            <Panel title="Money taken, by payment method">
              <div className="p-5">
                <BarList format={money} items={d.byMethod.map((m) => ({ label: PAYMENT_METHODS[m.method as PaymentMethod]?.label.split(' (')[0] ?? m.method, value: m.amountCents }))} />
                <p className="mt-3 text-sm text-muted">After change given and refunds. "Customer account" is sold on credit, not yet paid.</p>
              </div>
            </Panel>
            <Panel title="By cashier">
              <table className="w-full text-left">
                <tbody className="divide-y divide-line">
                  {d.byCashier.map((c) => (
                    <tr key={c.name}>
                      <td className="px-5 py-2">{c.name}</td>
                      <td className="px-3 py-2 text-right text-sm text-muted">{c.saleCount} sales</td>
                      <td className="px-5 py-2 text-right num">{money(c.netSalesCents)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Panel>
          </div>
          <Panel title="By category">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-line text-sm text-muted">
                  <th className="px-5 py-2 font-medium">Category</th>
                  <th className="px-3 py-2 text-right font-medium">Revenue excl. VAT</th>
                  {d.showsProfit && <th className="px-3 py-2 text-right font-medium">Cost</th>}
                  {d.showsProfit && <th className="px-5 py-2 text-right font-medium">Gross profit</th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-line">
                {d.byCategory.map((c) => (
                  <tr key={c.name}>
                    <td className="px-5 py-2">{c.name}</td>
                    <td className="px-3 py-2 text-right num">{money(c.revenueCents)}</td>
                    {d.showsProfit && <td className="px-3 py-2 text-right num">{money(c.costCents)}</td>}
                    {d.showsProfit && (
                      <td className="px-5 py-2 text-right num">
                        {money(c.grossProfitCents)} <span className="text-xs text-muted">{c.revenueCents ? `${Math.round((c.grossProfitCents / c.revenueCents) * 100)}%` : ''}</span>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </Panel>
          <Panel title="Best-selling products">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-line text-sm text-muted">
                  <th className="px-5 py-2 font-medium">Product</th>
                  <th className="px-3 py-2 text-right font-medium">Quantity</th>
                  <th className="px-3 py-2 text-right font-medium">Net sales</th>
                  {d.showsProfit && <th className="px-5 py-2 text-right font-medium">Gross profit</th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-line">
                {d.topProducts.map((p) => (
                  <tr key={p.productId}>
                    <td className="px-5 py-2">{p.name}</td>
                    <td className="px-3 py-2 text-right num">{formatQty(p.qtyMilli)} {p.unit === 'each' ? '' : p.unit}</td>
                    <td className="px-3 py-2 text-right num">{money(p.netSalesCents)}</td>
                    {d.showsProfit && <td className="px-5 py-2 text-right num">{money(p.grossProfitCents)}</td>}
                  </tr>
                ))}
              </tbody>
            </table>
          </Panel>
        </div>
      )}
    </Loader>
  );
}

interface ProfitData {
  revenueCents: number;
  costOfSalesCents: number;
  grossProfitCents: number;
  stockLossCents: number;
  wastageCents: number;
  expensesCents: number;
  pendingExpensesCents: number;
  netProfitCents: number;
  outputVatCents: number;
  expensesByCategory: { category: string; approvedCents: number; pendingCents: number }[];
}

function ProfitReport({ period }: { period: string }) {
  const money = useMoney();
  return (
    <Loader<ProfitData> url={`/api/reports/profit?${period}`}>
      {(d) => (
        <div className="grid gap-6 lg:grid-cols-[1.2fr_1fr]">
          <Panel title="Profit summary">
            <table className="w-full">
              <tbody className="divide-y divide-line">
                <Line label="Sales excluding VAT, after returns" value={money(d.revenueCents)} />
                <Line label="Cost of goods sold" value={`−${money(d.costOfSalesCents)}`} />
                <Line label="Gross profit" value={money(d.grossProfitCents)} strong />
                <Line label="Stock lost (adjustments and counts)" value={d.stockLossCents === 0 ? money(0) : d.stockLossCents > 0 ? `−${money(d.stockLossCents)}` : `+${money(-d.stockLossCents)}`} />
                {d.wastageCents !== 0 && <Line label="Wastage" value={`−${money(d.wastageCents)}`} />}
                <Line label="Approved expenses" value={d.expensesCents ? `−${money(d.expensesCents)}` : money(0)} />
                <Line label="Net profit (before tax)" value={money(d.netProfitCents)} strong tone={d.netProfitCents < 0 ? 'danger' : 'ok'} />
              </tbody>
            </table>
            <p className="border-t border-line px-5 py-3 text-sm text-muted">
              A management figure, not audited accounts. VAT collected on sales: {money(d.outputVatCents)}.
              {d.pendingExpensesCents > 0 && ` ${money(d.pendingExpensesCents)} of expenses are still waiting for approval and are not included.`}
            </p>
          </Panel>
          <Panel title="Expenses by category">
            <div className="p-5">
              {d.expensesByCategory.length ? <BarList format={money} items={d.expensesByCategory.map((e) => ({ label: e.category, value: e.approvedCents, note: e.pendingCents ? `${money(e.pendingCents)} pending` : undefined }))} /> : <p className="text-muted">No expenses in this period.</p>}
            </div>
          </Panel>
        </div>
      )}
    </Loader>
  );
}

function Line({ label, value, strong, tone }: { label: string; value: string; strong?: boolean; tone?: 'danger' | 'ok' }) {
  return (
    <tr className={strong ? 'font-semibold' : ''}>
      <td className="px-5 py-2.5">{label}</td>
      <td className={clsx('px-5 py-2.5 text-right num', tone === 'danger' && 'text-danger', tone === 'ok' && 'text-ok')}>{value}</td>
    </tr>
  );
}

interface StockData {
  valuation: { branch: string; category: string; valueCents: number; productsInStock: number }[];
  totalValueCents: number | null;
  lowStock: { productId: string; sku: string; name: string; unit: string; branch: string; qtyMilli: number; reorderLevelMilli: number }[];
  discrepancies: { reason: string; valueCents: number; documents: number; lines: number }[];
  netDiscrepancyCents: number;
  biggestLosses: { name: string; unit: string; qtyMilli: number; valueCents: number }[];
}

const REASONS: Record<string, string> = { ...ADJUSTMENT_REASONS, stocktake: 'Stocktake differences', wastage: 'Wastage' };

function StockReport({ period }: { period: string }) {
  const money = useMoney();
  return (
    <Loader<StockData> url={`/api/reports/stock?${period}`}>
      {(d) => (
        <div className="space-y-6">
          <Figures
            items={[
              { label: 'Stock value at cost', value: d.totalValueCents != null ? money(d.totalValueCents) : '—' },
              { label: 'Net stock gained or lost', value: money(d.netDiscrepancyCents), tone: d.netDiscrepancyCents < 0 ? 'danger' : undefined, note: 'In the period, from adjustments, counts and wastage' },
              { label: 'Items to reorder', value: String(d.lowStock.length), note: `${d.lowStock.filter((l) => l.qtyMilli <= 0).length} out of stock` },
            ]}
          />
          <div className="grid gap-6 lg:grid-cols-2">
            <Panel title="Stock discrepancies by reason">
              <table className="w-full text-left">
                <tbody className="divide-y divide-line">
                  {d.discrepancies.length === 0 && <tr><td className="px-5 py-4 text-muted">No adjustments or count differences in this period.</td></tr>}
                  {d.discrepancies.map((x) => (
                    <tr key={x.reason}>
                      <td className="px-5 py-2">{REASONS[x.reason as AdjustmentReason] ?? x.reason}<span className="block text-xs text-muted">{x.lines} lines on {x.documents} documents</span></td>
                      <td className={clsx('px-5 py-2 text-right num', x.valueCents < 0 && 'text-danger')}>{money(x.valueCents)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Panel>
            <Panel title="Biggest losses">
              <table className="w-full text-left">
                <tbody className="divide-y divide-line">
                  {d.biggestLosses.length === 0 && <tr><td className="px-5 py-4 text-muted">None.</td></tr>}
                  {d.biggestLosses.map((x) => (
                    <tr key={x.name}>
                      <td className="px-5 py-2">{x.name}</td>
                      <td className="px-3 py-2 text-right text-sm num">{formatQty(x.qtyMilli)}</td>
                      <td className="px-5 py-2 text-right text-danger num">{money(x.valueCents)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Panel>
          </div>
          <Panel title="Reorder list" actions={
            <Button size="sm" icon={<Download className="size-4" />} onClick={() => downloadCsv('reorder-list', ['Code', 'Product', 'Branch', 'In stock', 'Reorder level'], d.lowStock.map((l) => [l.sku, l.name, l.branch, formatQty(l.qtyMilli), formatQty(l.reorderLevelMilli)]))}>
              Download CSV
            </Button>
          }>
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-line text-sm text-muted">
                  <th className="px-5 py-2 font-medium">Product</th>
                  <th className="px-3 py-2 font-medium">Branch</th>
                  <th className="px-3 py-2 text-right font-medium">In stock</th>
                  <th className="px-5 py-2 text-right font-medium">Reorder at</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-line">
                {d.lowStock.map((l) => (
                  <tr key={`${l.productId}-${l.branch}`}>
                    <td className="px-5 py-2">{l.name}<span className="block text-xs text-muted">{l.sku}</span></td>
                    <td className="px-3 py-2 text-sm">{l.branch}</td>
                    <td className={clsx('px-3 py-2 text-right num', l.qtyMilli <= 0 ? 'text-danger' : 'text-warn')}>{formatQty(l.qtyMilli)}</td>
                    <td className="px-5 py-2 text-right num">{formatQty(l.reorderLevelMilli)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Panel>
          {d.valuation.length > 0 && (
            <Panel title="Stock value by branch and category">
              <table className="w-full text-left">
                <tbody className="divide-y divide-line">
                  {d.valuation.map((v) => (
                    <tr key={`${v.branch}-${v.category}`}>
                      <td className="px-5 py-2">{v.branch}</td>
                      <td className="px-3 py-2">{v.category}</td>
                      <td className="px-3 py-2 text-right text-sm text-muted">{v.productsInStock} products</td>
                      <td className="px-5 py-2 text-right num">{money(v.valueCents)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Panel>
          )}
        </div>
      )}
    </Loader>
  );
}

interface Ageing {
  current: number;
  d1_30: number;
  d31_60: number;
  d61_90: number;
  d90p: number;
  total: number;
  overdue: number;
}

function AgeingTable({ rows, totals, name }: { rows: { id: string; name: string; ageing: Ageing; balanceCents: number }[]; totals: Ageing; name: string }) {
  const money = useMoney();
  return (
    <Panel title={name} actions={
      <Button size="sm" icon={<Download className="size-4" />} onClick={() => downloadCsv(name.toLowerCase().replace(/\s+/g, '-'), ['Name', ...AGEING_BUCKETS.map((b) => b.label), 'Total'], rows.map((r) => [r.name, ...AGEING_BUCKETS.map((b) => dec(r.ageing[b.key])), dec(r.balanceCents)]))}>
        Download CSV
      </Button>
    }>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[760px] text-left">
          <thead>
            <tr className="border-b border-line text-sm text-muted">
              <th className="px-5 py-2 font-medium">Name</th>
              {AGEING_BUCKETS.map((b) => <th key={b.key} className="px-3 py-2 text-right font-medium">{b.label}</th>)}
              <th className="px-5 py-2 text-right font-medium">Total</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-line">
            {rows.map((r) => (
              <tr key={r.id}>
                <td className="px-5 py-2">{r.name}</td>
                {AGEING_BUCKETS.map((b) => (
                  <td key={b.key} className={clsx('px-3 py-2 text-right num', b.key !== 'current' && r.ageing[b.key] > 0 && 'text-danger')}>{r.ageing[b.key] ? money(r.ageing[b.key]) : ''}</td>
                ))}
                <td className="px-5 py-2 text-right font-medium num">{money(r.balanceCents)}</td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={7} className="px-5 py-4 text-muted">Nothing outstanding.</td></tr>}
          </tbody>
          <tfoot>
            <tr className="border-t border-line font-semibold">
              <td className="px-5 py-2">Total</td>
              {AGEING_BUCKETS.map((b) => <td key={b.key} className="px-3 py-2 text-right num">{money(totals[b.key])}</td>)}
              <td className="px-5 py-2 text-right num">{money(totals.total)}</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </Panel>
  );
}

function DebtorsReport() {
  const money = useMoney();
  return (
    <Loader<{ asOf: string; customers: { id: string; name: string; balanceCents: number; ageing: Ageing }[]; totals: Ageing }> url="/api/reports/debtors">
      {(d) => (
        <>
          <Figures items={[{ label: 'Customers owe', value: money(d.totals.total) }, { label: 'Overdue', value: money(d.totals.overdue), tone: d.totals.overdue ? 'danger' : undefined }, { label: 'Customers with a balance', value: String(d.customers.length) }]} />
          <AgeingTable name="Customer balances by age" rows={d.customers} totals={d.totals} />
        </>
      )}
    </Loader>
  );
}

function CreditorsReport() {
  const money = useMoney();
  return (
    <Loader<{ suppliers: { id: string; name: string; balanceCents: number; ageing: Ageing }[]; totals: Ageing & { dueThisWeek: number } }> url="/api/reports/creditors">
      {(d) => (
        <>
          <Figures
            items={[
              { label: 'We owe suppliers', value: money(d.totals.total) },
              { label: 'Overdue', value: money(d.totals.overdue), tone: d.totals.overdue ? 'danger' : undefined },
              { label: 'Due in the next 7 days', value: money(d.totals.dueThisWeek) },
            ]}
          />
          <AgeingTable name="Supplier balances by age" rows={d.suppliers} totals={d.totals} />
        </>
      )}
    </Loader>
  );
}

function CashReport({ period }: { period: string }) {
  return (
    <Loader<{ shifts: { id: string; number: string; status: string; cashier: string; till: string; branch: string; openedAt: string; cash: { currency: string; floatCents: number; expectedCents: number | null; countedCents: number | null; varianceCents: number | null }[] }[]; varianceByCurrency: Record<string, number> }> url={`/api/reports/cash?${period}`}>
      {(d) => (
        <>
          <Figures
            items={[
              { label: 'Shifts', value: String(d.shifts.length) },
              ...Object.entries(d.varianceByCurrency).map(([c, v]) => ({ label: `Net ${c} difference`, value: formatMoney(v, c as CurrencyCode), tone: v < 0 ? ('danger' as const) : undefined })),
              { label: 'Shifts short of cash', value: String(d.shifts.filter((s) => s.cash.some((c) => (c.varianceCents ?? 0) < 0)).length) },
            ]}
          />
          <Panel title="Shifts">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[720px] text-left">
                <thead>
                  <tr className="border-b border-line text-sm text-muted">
                    <th className="px-5 py-2 font-medium">Shift</th>
                    <th className="px-3 py-2 font-medium">Cashier</th>
                    <th className="px-3 py-2 text-right font-medium">Expected</th>
                    <th className="px-3 py-2 text-right font-medium">Counted</th>
                    <th className="px-5 py-2 text-right font-medium">Difference</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-line">
                  {d.shifts.flatMap((s) =>
                    s.cash.map((c, i) => (
                      <tr key={`${s.id}-${c.currency}`}>
                        <td className="px-5 py-2">{i === 0 && <>{s.number}<span className="block text-xs text-muted">{s.till}, {s.branch} — {new Date(s.openedAt).toLocaleDateString('en-GB')}</span></>}</td>
                        <td className="px-3 py-2 text-sm">{i === 0 ? s.cashier : ''}</td>
                        <td className="px-3 py-2 text-right num">{c.expectedCents != null ? formatMoney(c.expectedCents, c.currency as CurrencyCode) : 'Open'}</td>
                        <td className="px-3 py-2 text-right num">{c.countedCents != null ? formatMoney(c.countedCents, c.currency as CurrencyCode) : ''}</td>
                        <td className={clsx('px-5 py-2 text-right num', (c.varianceCents ?? 0) < 0 && 'text-danger', (c.varianceCents ?? 0) > 0 && 'text-warn')}>
                          {c.varianceCents == null ? '' : c.varianceCents === 0 ? 'Balanced' : formatMoney(c.varianceCents, c.currency as CurrencyCode)}
                        </td>
                      </tr>
                    )),
                  )}
                </tbody>
              </table>
            </div>
          </Panel>
        </>
      )}
    </Loader>
  );
}
